import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import currency from "currency.js";
import NumericTextField from "../../common/component/numeric/NumericTextField";

const EXTRA_FIELD = { name: "Add-on" };

function getExtraItems(extra) {
  if (!extra || !Array.isArray(extra.value)) return [];
  return extra.value;
}

function buildExtra(items) {
  if (!items.length) return null;
  const total = items.reduce((sum, o) => sum + (+o.pricing?.amount || 0), 0);
  return {
    name: items.map((o) => o.name).join(", "),
    pricing: { type: "fixed", amount: total },
    value: items,
    field: EXTRA_FIELD,
  };
}

export default function AddExtraCharge(props) {
  const { variant = {}, onChange } = props;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");

  const items = getExtraItems(variant.extra);

  const handleClose = () => {
    setName("");
    setAmount("");
    setOpen(false);
  };

  const emit = (nextItems) => {
    const next = { ...variant };
    const extra = buildExtra(nextItems);
    if (extra) {
      next.extra = extra;
    } else {
      delete next.extra;
    }
    onChange(next);
  };

  const canSubmit = Boolean(name?.trim()) && +amount > 0;
  const hasItems = items.length > 0;

  return (
    <Box mt={3}>
      <FormControl fullWidth variant="outlined">
        <InputLabel shrink={hasItems ? true : undefined} htmlFor="add-on-field">
          Add-on
        </InputLabel>
        <OutlinedInput
          id="add-on-field"
          label="Add-on"
          notched={hasItems ? true : undefined}
          readOnly
          onClick={() => setOpen(true)}
          value=""
          placeholder=""
          startAdornment={
            hasItems ? (
              <InputAdornment
                position="start"
                sx={{
                  flex: 1,
                  maxWidth: "100%",
                  margin: 0,
                  height: "auto",
                  maxHeight: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    py: 0.75,
                    width: "100%",
                    minWidth: 0,
                    alignItems: "center",
                  }}
                >
                  {items.map((item, idx) => (
                    <Chip
                      key={`${item.name}-${idx}`}
                      label={
                        +item.pricing?.amount > 0
                          ? `${item.name} (+${currency(item.pricing.amount).format()})`
                          : item.name
                      }
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onDelete={(e) => {
                        e?.stopPropagation?.();
                        emit(items.filter((_, i) => i !== idx));
                      }}
                    />
                  ))}
                </Box>
              </InputAdornment>
            ) : undefined
          }
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                aria-label="Add add-on"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(true);
                }}
              >
                <PlaylistAddIcon />
              </IconButton>
            </InputAdornment>
          }
          sx={{
            cursor: "pointer",
            width: "100%",
            ...(hasItems
              ? {
                  "& .MuiOutlinedInput-input": {
                    cursor: "pointer",
                    width: 0,
                    minWidth: 0,
                    padding: 0,
                    flex: "0 0 0",
                  },
                }
              : {
                  "& .MuiOutlinedInput-input": {
                    cursor: "pointer",
                  },
                }),
          }}
        />
      </FormControl>
      {open && (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>
            Add On
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box pt={2}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Name an extra and set a price—like decorations, a topper, or anything else.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    id="extra-name"
                    value={name}
                    label="Name"
                    name="name"
                    variant="outlined"
                    fullWidth
                    required
                    onChange={(e) => setName(e.target.value)}
                    inputProps={{ maxLength: 100 }}
                    placeholder="e.g. Decorations"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <NumericTextField
                      startIcon={<AttachMoneyOutlinedIcon />}
                      allowNegative={false}
                      decimalScale={2}
                      value={amount}
                      onChange={(value) => setAmount(value)}
                      label="Price"
                      variant="outlined"
                      required
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              disabled={!canSubmit}
              onClick={() => {
                const trimmed = name.trim();
                emit([
                  ...items,
                  { name: trimmed, pricing: { type: "fixed", amount: +amount } },
                ]);
                handleClose();
              }}
            >
              SAVE
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
